import { useEffect, useMemo, useState } from 'react';
import {
  DecisionAnswerDto,
  FieldPromptDto,
  FieldProblemDto,
  ScreenDto,
  SubmitScreenDto,
  fieldKey,
} from '../../lib/convertTypes';

interface Props {
  screen: ScreenDto;
  busy: boolean;
  problems: FieldProblemDto[];
  onSubmit: (dto: SubmitScreenDto) => void;
  onBack: () => void;
}

/**
 * Renders any review screen from its DTO — one component covers every mode, because screens
 * are generated from review items server-side (the wizard's own principle).
 */
export function ScreenRenderer({ screen, busy, problems, onSubmit, onBack }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [unitOrStack, setUnitOrStack] = useState('unit');
  const [substitution, setSubstitution] = useState(false);
  const [qiChoice, setQiChoice] = useState<string>('');
  const [aetbChoice, setAetbChoice] = useState<string>('');
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [showLoadEdit, setShowLoadEdit] = useState(false);
  const [avgLoad, setAvgLoad] = useState('');

  const hasWarnings = problems.some((p) => p.isWarning) && problems.every((p) => p.isWarning);

  // Seed the form with the offered defaults whenever the screen changes.
  useEffect(() => {
    const seeded: Record<string, string> = {};
    for (const f of screen.fields) seeded[fieldKey(f)] = f.defaultValue ?? '';
    setValues(seeded);
    setSubstitution(screen.substitution?.defaultAccept ?? false);
    setQiChoice(
      screen.profiles?.sheetQi ? 'sheet'
        : screen.profiles?.defaultQiIndex != null ? `profile:${screen.profiles.defaultQiIndex}`
        : 'custom');
    setAetbChoice(
      screen.profiles?.defaultAetbIndex != null
        ? `profile:${screen.profiles.defaultAetbIndex}`
        : 'custom');
    setDecisions({});
    setShowLoadEdit(false);
    setAvgLoad(screen.review?.averageGrossUnitLoad ?? '');
  }, [screen]);

  const problemFor = useMemo(() => {
    const map = new Map<string, FieldProblemDto>();
    for (const p of problems) map.set(`${p.field}|${p.param ?? ''}|${p.level ?? ''}`, p);
    return map;
  }, [problems]);

  const generalProblems = problems.filter(
    (p) => !screen.fields.some((f) => fieldKey(f) === `${p.field}|${p.param ?? ''}|${p.level ?? ''}`));

  const submitFields = (acceptWarnings: boolean) =>
    onSubmit({
      answers: screen.fields.map((f) => ({
        field: f.field, param: f.param, level: f.level, value: values[fieldKey(f)] ?? '',
      })),
      acceptWarnings,
    });

  const sectionTitle = `Step ${screen.step} of ${screen.totalSteps} — ${screen.title}`;

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">{sectionTitle}</div>
        <h2 className="text-xl font-semibold text-atg-navy">
          {screen.parameter} test
        </h2>
      </div>

      {screen.notes.map((note, i) => (
        <p key={i} className="text-sm bg-slate-100 border border-slate-200 rounded-md px-3 py-2">{note}</p>
      ))}

      {generalProblems.map((p, i) => (
        <p key={i} className={`text-sm rounded-md px-3 py-2 border ${
          p.isWarning ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-red-50 border-red-300 text-red-800'}`}>
          {p.message}
        </p>
      ))}

      {screen.kind === 'location' && (
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input type="radio" checked={unitOrStack === 'unit'} onChange={() => setUnitOrStack('unit')} />
            <span>Unit</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={unitOrStack === 'stackPipe'} onChange={() => setUnitOrStack('stackPipe')} />
            <span>Stack or pipe</span>
          </label>
          <SubmitRow busy={busy} onBack={onBack} onNext={() => onSubmit({ unitOrStack })} />
        </div>
      )}

      {screen.kind === 'levelValues' && screen.review && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <dl className="grid grid-cols-[minmax(240px,auto)_1fr] gap-x-6 gap-y-1 text-sm">
              {screen.review.rows.map((r, i) => (
                <span key={i} className="contents">
                  <dt className="text-slate-500">{r.section} — {r.label}</dt>
                  <dd className="font-mono">{r.value}</dd>
                </span>
              ))}
            </dl>
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 rounded bg-atg-navy text-white disabled:opacity-50"
              disabled={busy} onClick={() => onSubmit({ action: 'accept' })}>
              Use these values for every level
            </button>
            <button className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
              disabled={busy} onClick={() => onSubmit({ action: 'decline' })}>
              Ask on each level's screens instead
            </button>
            <button className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
              disabled={busy} onClick={onBack}>
              Back
            </button>
          </div>
        </div>
      )}

      {(screen.kind === 'testDetails' || screen.kind === 'protocolGas' || screen.kind === 'flowLevel') && (
        <div className="space-y-4">
          {groupFields(screen.fields).map(([group, fields]) => (
            <fieldset key={group ?? 'test'} className="border border-slate-200 rounded-md p-4 space-y-3 bg-white">
              {group && <legend className="text-sm font-semibold text-slate-600 px-1">{group}</legend>}
              {fields.map((f) => (
                <FieldInput
                  key={fieldKey(f)}
                  field={f}
                  value={values[fieldKey(f)] ?? ''}
                  problem={problemFor.get(fieldKey(f))}
                  onChange={(v) => setValues((prev) => ({ ...prev, [fieldKey(f)]: v }))}
                />
              ))}
            </fieldset>
          ))}
          <SubmitRow
            busy={busy}
            onBack={onBack}
            onNext={() => submitFields(hasWarnings)}
            nextLabel={hasWarnings ? 'Use values as typed' : 'Continue'}
          />
        </div>
      )}

      {screen.kind === 'substitution' && screen.substitution && (
        <div className="space-y-4">
          <p className="text-sm">{screen.substitution.explanation}</p>
          <table className="text-sm border-collapse">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pr-6 py-1">Run</th>
                <th className="py-1">Original reference value</th>
              </tr>
            </thead>
            <tbody>
              {screen.substitution.affectedRuns.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="pr-6 py-1">{r.runNumber}</td>
                  <td className="py-1 font-mono">{r.originalValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={substitution} onChange={(e) => setSubstitution(e.target.checked)} />
            <span>Replace these reference values with 0.01 so a relative accuracy can be computed</span>
          </label>
          <SubmitRow busy={busy} onBack={onBack} onNext={() => onSubmit({ substitutionAccepted: substitution })} />
        </div>
      )}

      {screen.kind === 'profiles' && screen.profiles && (
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-semibold">Who was the qualified individual (QI) for this test?</h3>
            {screen.profiles.sheetQi && (
              <RadioRow name="qi" value="sheet" current={qiChoice} onPick={setQiChoice}
                label={`From this workbook: ${screen.profiles.sheetQi.firstName ?? ''} ${screen.profiles.sheetQi.lastName ?? ''}`
                  + (screen.profiles.sheetQi.examDate ? ` (exam ${screen.profiles.sheetQi.examDate})` : '')} />
            )}
            {screen.profiles.qiProfiles.map((p) => (
              <RadioRow key={p.index} name="qi" value={`profile:${p.index}`} current={qiChoice} onPick={setQiChoice}
                label={`${p.firstName ?? ''} ${p.lastName ?? ''}${p.examDate ? ` (exam ${p.examDate})` : ''}`} />
            ))}
            <RadioRow name="qi" value="custom" current={qiChoice} onPick={setQiChoice} label="Enter a new person…" />
            {qiChoice === 'custom' && (
              <CustomFields
                prefix="qi"
                custom={custom}
                setCustom={setCustom}
                fields={[
                  ['lastName', 'QI last name', 'Jensen'],
                  ['firstName', 'QI first name', 'Alan'],
                  ['middleInitial', 'QI middle initial (optional)', ''],
                  ['examDate', 'QI exam date', '2024-02-15'],
                  ['providerName', 'Certification provider name', 'Source Evaluation Society'],
                  ['providerEmail', 'Certification provider email', 'qstiprogram@gmail.com'],
                ]}
              />
            )}
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">Which air emission testing body (AETB) performed this test?</h3>
            {screen.profiles.aetbProfiles.map((p) => (
              <RadioRow key={p.index} name="aetb" value={`profile:${p.index}`} current={aetbChoice} onPick={setAetbChoice}
                label={p.name ?? '(unnamed)'} />
            ))}
            <RadioRow name="aetb" value="custom" current={aetbChoice} onPick={setAetbChoice} label="Enter a new company…" />
            {aetbChoice === 'custom' && (
              <CustomFields
                prefix="aetb"
                custom={custom}
                setCustom={setCustom}
                fields={[
                  ['name', 'AETB company name', 'Alliance Technical Group'],
                  ['phone', 'AETB phone number', '256-351-0121'],
                  ['email', 'AETB email', 'quality@alliancetg.com'],
                ]}
              />
            )}
          </div>
          <SubmitRow busy={busy} onBack={onBack} onNext={() => onSubmit(profilesSubmit(qiChoice, aetbChoice, custom))} />
        </div>
      )}

      {screen.kind === 'flowVerdict' && (
        <div className="space-y-4">
          {screen.fields.map((f) => (
            <FieldInput
              key={fieldKey(f)}
              field={f}
              value={values[fieldKey(f)] ?? ''}
              problem={problemFor.get(fieldKey(f))}
              onChange={(v) => setValues((prev) => ({ ...prev, [fieldKey(f)]: v }))}
            />
          ))}
          {screen.decisions.map((d) => (
            <div key={d.id} className="bg-white border border-amber-300 rounded-md p-4 space-y-2">
              <p className="text-sm">{d.explanation}</p>
              {d.field && (
                <FieldInput
                  field={d.field}
                  value={decisions[d.id] ?? d.field.defaultValue ?? ''}
                  problem={undefined}
                  onChange={(v) => setDecisions((prev) => ({ ...prev, [d.id]: v }))}
                />
              )}
            </div>
          ))}
          <SubmitRow busy={busy} onBack={onBack} onNext={() =>
            onSubmit({
              answers: screen.fields.map((f) => ({
                field: f.field, param: f.param, level: f.level, value: values[fieldKey(f)] ?? '',
              })),
              decisions: screen.decisions
                .filter((d) => d.kind !== 'impossible')
                .map((d) => ({ id: d.id, value: decisions[d.id] ?? d.field?.defaultValue ?? '' })),
            })} nextLabel="Write the file" />
        </div>
      )}

      {screen.kind === 'review' && screen.review?.flowLevels && (
        <div className="space-y-4">
          {['Test', 'Who ran the test'].map((section) => {
            const rows = screen.review!.rows.filter((r) => r.section === section);
            if (rows.length === 0) return null;
            return (
              <div key={section} className="bg-white border border-slate-200 rounded-md p-4">
                <h3 className="font-semibold text-slate-600 mb-2">{section}</h3>
                <dl className="grid grid-cols-[minmax(200px,auto)_1fr] gap-x-6 gap-y-1 text-sm">
                  {rows.map((r, i) => (
                    <span key={i} className="contents">
                      <dt className="text-slate-500">{r.label}</dt>
                      <dd className="font-mono">{r.value}</dd>
                    </span>
                  ))}
                </dl>
              </div>
            );
          })}
          {screen.review.flowLevels.map((level) => (
            <div key={level.axis} className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
              <h3 className="font-semibold text-slate-600">{level.headline}</h3>
              <table className="text-sm border-collapse w-full font-mono">
                <thead>
                  <tr className="text-left text-slate-500 font-sans">
                    <th className="py-1 pr-4">Run</th>
                    <th className="py-1 pr-4">CEM</th>
                    <th className="py-1 pr-4">Ref</th>
                    <th className="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {level.runs.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-1 pr-4">{r.runNumber}</td>
                      <td className="py-1 pr-4">{r.cemValue}</td>
                      <td className="py-1 pr-4">{r.referenceValue}</td>
                      <td className="py-1">{r.status ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {level.notes.map((n, i) => (
                <p key={i} className="text-xs text-slate-500">{n}</p>
              ))}
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <button className="px-4 py-2 rounded bg-atg-navy text-white disabled:opacity-50"
              disabled={busy} onClick={() => onSubmit({ action: 'write' })}>
              Write the file
            </button>
            <button className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
              disabled={busy} onClick={onBack}>
              Go back through this test's answers
            </button>
            <button className="px-4 py-2 rounded border border-red-300 text-red-700 disabled:opacity-50"
              disabled={busy} onClick={() => onSubmit({ action: 'skip' })}>
              Skip this test — write nothing
            </button>
          </div>
        </div>
      )}

      {screen.kind === 'review' && screen.review && !screen.review.flowLevels && (
        <div className="space-y-4">
          {['Test', 'Protocol gas', 'Who ran the test'].map((section) => {
            const rows = screen.review!.rows.filter((r) => r.section === section);
            if (rows.length === 0) return null;
            return (
              <div key={section} className="bg-white border border-slate-200 rounded-md p-4">
                <h3 className="font-semibold text-slate-600 mb-2">{section}</h3>
                <dl className="grid grid-cols-[minmax(200px,auto)_1fr] gap-x-6 gap-y-1 text-sm">
                  {rows.map((r, i) => (
                    <span key={i} className="contents">
                      <dt className="text-slate-500">{r.label}</dt>
                      <dd className="font-mono">{r.value}</dd>
                    </span>
                  ))}
                </dl>
              </div>
            );
          })}

          <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
            <div className="text-sm">
              Average gross unit load: <span className="font-mono">{screen.review.averageGrossUnitLoad ?? '(blank)'}</span>
            </div>
            <p className="text-xs text-slate-500">{screen.review.loadAdvice}</p>
            <table className="text-sm border-collapse w-full">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-1 pr-4">Run</th>
                  <th className="py-1 pr-4">CEM</th>
                  <th className="py-1 pr-4">Ref</th>
                  <th className="py-1 pr-4">Load</th>
                  <th className="py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {screen.review.runs.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 font-mono">
                    <td className="py-1 pr-4">{r.runNumber}</td>
                    <td className="py-1 pr-4">{r.cemValue}</td>
                    <td className="py-1 pr-4">{r.referenceValue}</td>
                    <td className="py-1 pr-4">{r.load ?? '—'}</td>
                    <td className="py-1">{r.status ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showLoadEdit && (
            <div className="bg-white border border-amber-300 rounded-md p-4 space-y-2">
              <label className="block text-sm">
                Average gross unit load (whole number, e.g. 154)
                <input className="mt-1 block w-48 border border-slate-300 rounded px-2 py-1"
                  value={avgLoad} onChange={(e) => setAvgLoad(e.target.value)} />
              </label>
              <button
                className="px-3 py-1.5 rounded bg-atg-navy text-white text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => onSubmit({ action: 'editLoads', averageGrossUnitLoad: avgLoad })}
              >
                Apply load change
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button className="px-4 py-2 rounded bg-atg-navy text-white disabled:opacity-50"
              disabled={busy} onClick={() => onSubmit({ action: 'write' })}>
              Write the file
            </button>
            <button className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
              disabled={busy} onClick={() => setShowLoadEdit((v) => !v)}>
              Change the unit loads
            </button>
            <button className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
              disabled={busy} onClick={onBack}>
              Go back through this test's answers
            </button>
            <button className="px-4 py-2 rounded border border-red-300 text-red-700 disabled:opacity-50"
              disabled={busy} onClick={() => onSubmit({ action: 'skip' })}>
              Skip this test — write nothing
            </button>
          </div>
        </div>
      )}

      {screen.kind === 'decisions' && (
        <div className="space-y-4">
          {screen.decisions.map((d) => (
            <div key={d.id} className="bg-white border border-amber-300 rounded-md p-4 space-y-2">
              <p className="text-sm">{d.explanation}</p>
              {d.kind === 'confirm' && d.choices && (
                <div className="space-y-1">
                  <p className="font-medium text-sm">{d.question}</p>
                  {d.choices.map((c) => (
                    <RadioRow key={c.value} name={d.id} value={c.value} current={decisions[d.id] ?? ''}
                      onPick={(v) => setDecisions((prev) => ({ ...prev, [d.id]: v }))} label={c.label} />
                  ))}
                </div>
              )}
              {d.kind === 'choice' && d.choices && (
                <div className="space-y-1">
                  <p className="font-medium text-sm">{d.question}</p>
                  {d.choices.map((c) => (
                    <RadioRow key={c.value} name={d.id} value={c.value} current={decisions[d.id] ?? ''}
                      onPick={(v) => setDecisions((prev) => ({ ...prev, [d.id]: v }))} label={c.label} />
                  ))}
                </div>
              )}
              {d.kind === 'field' && d.field && (
                <FieldInput
                  field={d.field}
                  value={decisions[d.id] ?? d.field.defaultValue ?? ''}
                  problem={undefined}
                  onChange={(v) => setDecisions((prev) => ({ ...prev, [d.id]: v }))}
                />
              )}
              {d.kind === 'impossible' && (
                <p className="text-sm text-red-700">No answer here can fix this — the input file has to change.</p>
              )}
            </div>
          ))}
          {screen.decisions.some((d) => d.kind !== 'impossible') && (
            <SubmitRow busy={busy} onBack={onBack} onNext={() =>
              onSubmit({
                decisions: screen.decisions
                  .filter((d) => d.kind !== 'impossible')
                  .map<DecisionAnswerDto>((d) => ({ id: d.id, value: decisions[d.id] })),
              })} />
          )}
        </div>
      )}

      {(screen.kind === 'done' || screen.kind === 'failed') && screen.result && (
        <ResultPanel result={screen.result} />
      )}
    </div>
  );
}

function SubmitRow({ busy, onBack, onNext, nextLabel }: {
  busy: boolean; onBack: () => void; onNext: () => void; nextLabel?: string;
}) {
  return (
    <div className="flex gap-2">
      <button
        className="px-4 py-2 rounded bg-atg-navy text-white disabled:opacity-50"
        disabled={busy}
        onClick={onNext}
      >
        {nextLabel ?? 'Continue'}
      </button>
      <button
        className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
        disabled={busy}
        onClick={onBack}
      >
        Back
      </button>
    </div>
  );
}

function ResultPanel({ result }: { result: NonNullable<ScreenDto['result']> }) {
  const verdictColor =
    result.rataVerdict === 'FAILED' ? 'text-red-700'
      : result.rataVerdict ? 'text-green-700'
      : 'text-slate-600';
  return (
    <div className="space-y-3">
      {result.status === 'success' ? (
        <>
          <p className={`text-lg font-semibold ${verdictColor}`}>
            RATA result: {result.rataVerdict ?? '(none recorded)'}
          </p>
          <dl className="grid grid-cols-[minmax(240px,auto)_1fr] gap-x-6 gap-y-1 text-sm">
            {result.statistics.map((s) => (
              <span key={s.label} className="contents">
                <dt className="text-slate-500">{s.label}</dt>
                <dd className="font-mono">{s.value}</dd>
              </span>
            ))}
          </dl>
          <p className="text-green-700 text-sm">
            ✓ File is EPA-ready — it passed every ECMPS format check: <b>{result.outputFileName}</b>
          </p>
        </>
      ) : (
        <div className="space-y-1">
          {result.errors.map((e, i) => (
            <p key={i} className="text-sm text-red-700">{e}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function groupFields(fields: FieldPromptDto[]): [string | undefined, FieldPromptDto[]][] {
  const groups: [string | undefined, FieldPromptDto[]][] = [];
  for (const f of fields) {
    const last = groups[groups.length - 1];
    if (last && last[0] === f.group) last[1].push(f);
    else groups.push([f.group, [f]]);
  }
  return groups;
}

function FieldInput({ field, value, problem, onChange }: {
  field: FieldPromptDto;
  value: string;
  problem?: FieldProblemDto;
  onChange: (v: string) => void;
}) {
  const sourceBadge =
    field.defaultSource === 'sheet' ? 'from the file'
      : field.defaultSource === 'carried' ? 'confirmed earlier this session'
      : field.defaultSource === 'entered' ? 'as you entered it'
      : field.defaultSource === 'house' ? 'ATG convention'
      : undefined;
  return (
    <label className="block text-sm">
      <span className="flex items-center gap-2">
        {field.question}
        {sourceBadge && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{sourceBadge}</span>
        )}
      </span>
      {field.note && <span className="block text-xs text-amber-800 mt-0.5">{field.note}</span>}
      {field.kind === 'choice' && field.choices ? (
        <span className="mt-1 block space-y-1">
          {field.choices.map((c) => (
            <span key={c.value} className="flex items-center gap-2">
              <input
                type="radio"
                name={fieldKey(field)}
                checked={value === c.value}
                onChange={() => onChange(c.value)}
              />
              <span>{c.label}</span>
            </span>
          ))}
        </span>
      ) : field.kind === 'cemPaste' ? (
        <textarea
          className={`mt-1 block w-full max-w-md border rounded px-2 py-1 font-mono min-h-24 ${
            problem && !problem.isWarning ? 'border-red-400' : 'border-slate-300'}`}
          value={value}
          placeholder={field.example ?? undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className={`mt-1 block w-full max-w-md border rounded px-2 py-1 font-mono ${
            problem && !problem.isWarning ? 'border-red-400' : 'border-slate-300'}`}
          value={value}
          placeholder={field.example ?? undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {problem && (
        <span className={`block text-xs mt-0.5 ${problem.isWarning ? 'text-amber-800' : 'text-red-700'}`}>
          {problem.message}
        </span>
      )}
    </label>
  );
}

function RadioRow({ name, value, current, onPick, label }: {
  name: string; value: string; current: string; onPick: (v: string) => void; label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="radio" name={name} checked={current === value} onChange={() => onPick(value)} />
      <span>{label}</span>
    </label>
  );
}

function CustomFields({ prefix, custom, setCustom, fields }: {
  prefix: string;
  custom: Record<string, string>;
  setCustom: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  fields: [string, string, string][];
}) {
  return (
    <div className="pl-6 space-y-2">
      {fields.map(([key, label, example]) => (
        <label key={key} className="block text-sm">
          {label}
          <input
            className="mt-1 block w-full max-w-md border border-slate-300 rounded px-2 py-1"
            value={custom[`${prefix}.${key}`] ?? ''}
            placeholder={example || undefined}
            onChange={(e) => setCustom((prev) => ({ ...prev, [`${prefix}.${key}`]: e.target.value }))}
          />
        </label>
      ))}
    </div>
  );
}

function profilesSubmit(
  qiChoice: string, aetbChoice: string, custom: Record<string, string>): SubmitScreenDto {
  const dto: SubmitScreenDto = {};
  if (qiChoice === 'sheet') dto.qiFromSheet = true;
  else if (qiChoice.startsWith('profile:')) dto.qiProfileIndex = Number(qiChoice.slice(8));
  else {
    dto.qiCustom = {
      index: -1,
      lastName: custom['qi.lastName'],
      firstName: custom['qi.firstName'],
      middleInitial: custom['qi.middleInitial'],
      examDate: custom['qi.examDate'],
      providerName: custom['qi.providerName'],
      providerEmail: custom['qi.providerEmail'],
    };
  }
  if (aetbChoice.startsWith('profile:')) dto.aetbProfileIndex = Number(aetbChoice.slice(8));
  else {
    dto.aetbCustom = {
      index: -1,
      name: custom['aetb.name'],
      phone: custom['aetb.phone'],
      email: custom['aetb.email'],
    };
  }
  return dto;
}
